import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, X as XIcon, Plus, Trash2, Mail, Save } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AlertRecipientsModal — Phase 8B per-agency email-alert recipient editor.
 *
 * Lists every row of `organizations` and lets an admin edit
 * `alert_emails: list[str]` per agency. Saves via PUT /organizations/{id}
 * (preserves the rest of the org payload).
 *
 * Includes a "Send test" button per row that triggers the test endpoint
 * with `agency_id` so the admin can verify routing without leaving the
 * modal.
 */
export default function AlertRecipientsModal({ open, onClose }) {
  const [loading, setLoading] = useState(false);
  const [orgs, setOrgs] = useState([]);
  const [editing, setEditing] = useState({}); // { [org_id]: ["a@b.com", ...] }
  const [savingId, setSavingId] = useState(null);
  const [testingId, setTestingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/organizations`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json();
      const list = j.organizations || [];
      setOrgs(list);
      const seed = {};
      for (const o of list) seed[o.id] = [...(o.alert_emails || [])];
      setEditing(seed);
    } catch (e) {
      toast.error(`Failed to load agencies: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load();
  }, [open]);

  if (!open) return null;

  const addRow = (orgId) => {
    setEditing((s) => ({ ...s, [orgId]: [...(s[orgId] || []), ""] }));
  };

  const removeRow = (orgId, idx) => {
    setEditing((s) => ({
      ...s,
      [orgId]: (s[orgId] || []).filter((_, i) => i !== idx),
    }));
  };

  const setRow = (orgId, idx, value) => {
    setEditing((s) => ({
      ...s,
      [orgId]: (s[orgId] || []).map((e, i) => (i === idx ? value : e)),
    }));
  };

  const isValidEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((e || "").trim());

  const save = async (org) => {
    const cleaned = (editing[org.id] || [])
      .map((e) => (e || "").trim())
      .filter(Boolean);
    const invalid = cleaned.filter((e) => !isValidEmail(e));
    if (invalid.length) {
      toast.error(`Invalid email${invalid.length > 1 ? "s" : ""}: ${invalid.join(", ")}`);
      return;
    }
    setSavingId(org.id);
    try {
      const body = {
        name: org.name,
        address_line1: org.address_line1 || "",
        city: org.city || "",
        state: org.state || "WI",
        zip: org.zip || "",
        phone: org.phone || "",
        fax: org.fax || "",
        provider_number: org.provider_number || "",
        npi: org.npi || "",
        is_default: !!org.is_default,
        alert_emails: cleaned,
      };
      const r = await fetch(`${API}/organizations/${org.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const t = await r.text().catch(() => "");
        throw new Error(t || `HTTP ${r.status}`);
      }
      const updated = await r.json();
      setOrgs((list) =>
        list.map((o) => (o.id === org.id ? { ...o, ...updated } : o))
      );
      setEditing((s) => ({ ...s, [org.id]: [...(updated.alert_emails || [])] }));
      toast.success(
        cleaned.length
          ? `${cleaned.length} recipient${cleaned.length > 1 ? "s" : ""} saved for ${org.name}`
          : `Recipients cleared for ${org.name} — fallback will be used`
      );
    } catch (e) {
      toast.error(`Save failed: ${e.message}`);
    } finally {
      setSavingId(null);
    }
  };

  const sendTest = async (orgId) => {
    setTestingId(orgId);
    try {
      const r = await fetch(`${API}/admin/email/test`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ agency_id: orgId }),
      });
      const j = await r.json().catch(() => ({}));
      if (j.delivery?.skipped === "no_gmail_password_configured") {
        toast.info("No GMAIL_APP_PASSWORD configured — email built but not delivered");
      } else if (j.delivery?.skipped === "no_recipients_resolved") {
        toast.error("No recipients — set this agency's alert_emails first or set EMAIL_ALERT_FALLBACK_RECIPIENTS");
      } else if (j.ok) {
        toast.success(`Test email sent to ${(j.recipients || []).join(", ")} (${j.recipient_source})`);
      } else {
        toast.error(`Test failed: ${j.delivery?.error || "unknown"}`);
      }
    } catch (e) {
      toast.error(`Test failed: ${e.message}`);
    } finally {
      setTestingId(null);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="alert-recipients-modal"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-xl max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between gap-4 px-6 py-4 border-b border-[#D6E3EF]">
          <div>
            <div className="text-[11px] uppercase tracking-[0.16em] text-[#3A5275] font-semibold">
              Phase 8B · Email alerts
            </div>
            <h2 className="font-display text-[20px] font-bold text-[#0B1E3A] mt-1">
              Per-agency alert recipients
            </h2>
            <p className="text-[12px] text-[#3A5275] mt-1 max-w-xl">
              These addresses receive PA-denied + Avida-sync-failed alerts for
              each agency. Empty list falls back to{" "}
              <code className="text-[11px]">EMAIL_ALERT_FALLBACK_RECIPIENTS</code>.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-[#3A5275] hover:text-[#0B1E3A] p-1 rounded-md"
            data-testid="alert-recipients-close"
            aria-label="Close"
          >
            <XIcon className="w-5 h-5" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-6 space-y-5" data-testid="alert-recipients-list">
          {loading ? (
            <div className="flex items-center gap-2 text-[#3A5275]">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading agencies…
            </div>
          ) : orgs.length === 0 ? (
            <div className="text-[14px] text-[#3A5275]" data-testid="alert-recipients-empty">
              No agencies registered yet. Add one via{" "}
              <code>POST /api/organizations</code>.
            </div>
          ) : (
            orgs.map((org) => {
              const rows = editing[org.id] || [];
              const dirty =
                JSON.stringify(rows.filter(Boolean)) !==
                JSON.stringify(org.alert_emails || []);
              return (
                <article
                  key={org.id}
                  className="rounded-xl border border-[#D6E3EF] p-4"
                  data-testid={`alert-recipients-org-${org.id}`}
                >
                  <header className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-display text-[15px] font-bold text-[#0B1E3A]">
                        {org.name}
                      </div>
                      <div className="text-[11px] text-[#3A5275] mt-0.5">
                        Provider #{org.provider_number || "—"} · NPI {org.npi || "—"}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => sendTest(org.id)}
                      disabled={testingId === org.id}
                      className="btn-ghost !h-8 !text-[12px]"
                      data-testid={`alert-recipients-test-${org.id}`}
                      title="Send a test alert to this agency's recipients (or fallback if empty)"
                    >
                      {testingId === org.id ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Mail className="w-3.5 h-3.5" />
                      )}
                      Test
                    </button>
                  </header>

                  <div className="mt-3 space-y-2">
                    {rows.length === 0 && (
                      <div className="text-[12px] text-[#8A6B00] bg-[#FFFDEC] border border-[#DBB90A] rounded-md px-3 py-2">
                        No recipients yet — alerts for this agency will use the
                        fallback list.
                      </div>
                    )}
                    {rows.map((email, idx) => (
                      <div
                        key={idx}
                        className="flex items-center gap-2"
                        data-testid={`alert-recipients-row-${org.id}-${idx}`}
                      >
                        <input
                          type="email"
                          value={email}
                          onChange={(e) => setRow(org.id, idx, e.target.value)}
                          placeholder="coord@agency.com"
                          className="tb-input !h-9 flex-1"
                          data-testid={`alert-recipients-input-${org.id}-${idx}`}
                        />
                        <button
                          type="button"
                          onClick={() => removeRow(org.id, idx)}
                          className="text-[#c05746] hover:bg-[#fbeae5] rounded-md p-1.5"
                          data-testid={`alert-recipients-remove-${org.id}-${idx}`}
                          aria-label="Remove recipient"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => addRow(org.id)}
                      className="text-[12px] text-[#0A3D79] hover:underline inline-flex items-center gap-1 font-semibold"
                      data-testid={`alert-recipients-add-${org.id}`}
                    >
                      <Plus className="w-3.5 h-3.5" /> Add recipient
                    </button>
                  </div>

                  <div className="mt-3 flex justify-end">
                    <button
                      type="button"
                      onClick={() => save(org)}
                      disabled={!dirty || savingId === org.id}
                      className="btn-primary !h-8 !text-[12px] disabled:opacity-50"
                      data-testid={`alert-recipients-save-${org.id}`}
                    >
                      {savingId === org.id ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Save className="w-3.5 h-3.5" />
                      )}
                      Save changes
                    </button>
                  </div>
                </article>
              );
            })
          )}
        </div>

        <footer className="px-6 py-3 border-t border-[#D6E3EF] bg-[#F7FAFD] flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="btn-ghost"
            data-testid="alert-recipients-cancel"
          >
            Close
          </button>
        </footer>
      </div>
    </div>
  );
}
